import { Mark, Node, Schema } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { Transform } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";

import type { MenuItemState } from "../../core/menu";
import type {
    MarkdownSerializerConfig,
    NodeSerializerSpec,
} from "../../core/types";
import { createInfoPlugin } from "./info-popup";
import { createInsertPlugin, createMenu } from "./insert-menu";
import type { LinkedEntitiesManager } from "./manager";

export interface MatchInput {
    start: number;
    end: number;
    match?: RegExpMatchArray & {
        groups: {
            query: string;
        };
    };
}

export interface LinkedEntity<Type = string, Data = unknown> {
    name: string;
    id: string;
    group?: string;
    order_index?: number;
    orgName?: string;
    stekName?: string;
    href: string;
    type: Type;
    state: "pending" | "ready" | "gone" | "external";
    data?: Data;
}

export interface EntityDomSpec<T extends LinkedEntity = LinkedEntity> {
    ["data-type"]: T["type"];
    ["data-entity-id"]: T["id"];
    ["data-name"]: T["name"];
    ["data-state"]: T["state"];
    href: string;
    class: string;
    part: string;
    [key: string]: string | number | symbol | boolean;
}

/**
 * Creates a schema modifier with the given nodetype as upgrade for links.
 */
export const createLinkUpgradeSchemaModifier = (
    nodeType: string,
    isLink: boolean,
    popup: {
        key: string;
        hover: boolean;
    },
    prefix: string = ""
) => {
    return (currentSchema: Schema) => {
        return new Schema({
            nodes: currentSchema.spec.nodes
                .addToEnd(nodeType, {
                    attrs: {
                        type: { default: "undefined" },
                        name: {},
                        entityId: {},
                        href: { default: "" },
                        state: { default: "ready" },
                        popupHover: { default: popup.hover },
                        popupKey: { default: popup.key },
                        brokenLink: { default: false },
                    },
                    toDOM(node) {
                        const entityType =
                            node.attrs.state === "gone"
                                ? "deleted"
                                : node.attrs.type;
                        return [
                            isLink ? "a" : "span",
                            {
                                "class": `linked-entity ${nodeType}`,
                                "href": node.attrs?.href,
                                "data-type": entityType,
                                "data-entity-id": node.attrs.entityId,
                                "data-name": node.attrs.name,
                                "data-state": node.attrs.state,
                                "part": `${nodeType} ${nodeType}-${entityType}`,
                            } as EntityDomSpec,
                            node.attrs.state === "gone"
                                ? `${node.attrs.name} (deleted)`
                                : `${prefix}${node.attrs.name}`,
                        ];
                    },
                    parseDOM: [
                        {
                            tag: `.${nodeType}`,
                            getAttrs(domNode: HTMLElement) {
                                return {
                                    type: domNode.dataset.originalType,
                                    entityId: domNode.dataset.entityId,
                                    name: domNode.dataset.name,
                                    state: domNode.dataset.state,
                                    href: domNode.getAttribute("href"),
                                };
                            },
                        },
                    ],
                    inline: true,
                    group: "inline",
                    draggable: false,
                    selectable: true,
                    leafText(node) {
                        return `${prefix}${node.attrs.name}`;
                    },
                })
                .update("heading", {
                    ...currentSchema.nodes.heading.spec,
                    content: `(text | image | ${nodeType})*`,
                }),
            marks: currentSchema.spec.marks,
        });
    };
};

export const createModifySerializer = (
    nodeType: string,
    getManager: () => LinkedEntitiesManager
) => {
    return (currentConfig: MarkdownSerializerConfig) => {
        return {
            ...currentConfig,
            nodes: {
                ...currentConfig.nodes,
                [nodeType]: ((state, node) => {
                    const manager = getManager();
                    state.write(manager.serialize(node.attrs.entityId, node));
                }) as NodeSerializerSpec,
            },
        };
    };
};

export const createUpgradeParsedDocument = (
    nodeType: string,
    getManager: () => LinkedEntitiesManager
) => {
    return async (doc: Node, schema: Schema) => {
        const transform = new Transform(doc);
        const entitiesManager = getManager();

        doc.descendants((node: Node, pos) => {
            const linkMark: Mark | undefined = node.marks.find(
                (mark) => mark.type === schema.marks.link
            );
            if (node.isText && linkMark && linkMark.attrs.href) {
                // The name that is saved in the document for a given entity might
                // not be up-to-date with the name of the linked entity that is
                // extracted from the repository. If there is a new name, it will be
                // overwritten with the name that is in the repository, however if
                // there is not a linked entity in the repository for some reason,
                // the old associated name will be preserved, making it easier to
                // figure out what the link was supposed to link to.
                const staticEntityName = node.text || "";

                if (
                    !entitiesManager.check(
                        linkMark.attrs.href,
                        staticEntityName
                    )
                ) {
                    return;
                }

                const entity = entitiesManager.extract(
                    linkMark.attrs.href,
                    staticEntityName
                );

                // If there are mulitple links being upgraded in this loop, the positions
                // will have shifted. Therefore we need to compute where the link is in the
                // current document using Transform.mapping.map()
                transform.replaceRangeWith(
                    transform.mapping.map(pos),
                    transform.mapping.map(pos + node.nodeSize),
                    schema.node(nodeType, {
                        entityId: entity.id,
                        ...entity,
                    })
                );
                return false;
            }
        });
        return transform.doc;
    };
};

/**
 * Creates a simple extension for upgrading links with popups.
 *
 * Can be used as base class for an actual extension. For example:
 * ```
 *     const BaseExtension = createEntityExtension(
 *         "business_entity", {
 *             key: "businessEntityDetails",
 *             hover: true,
 *         }
 *     )
 *     class MyEntityExtension extends BaseExtension {...}
 * ```
 */
export const createEntityExtension = <E extends LinkedEntity = LinkedEntity>(
    // Named type which your linking, e.g. process, product, domain, role etc.
    nodeType: string,
    // The base URL which should be included to match URL's.
    baseUrl: string,
    // Whether it actually links somewhere.
    isLink: boolean,
    // All settings for displaying the link.
    info: {
        // Popup settings for hovering over the element.
        popup: {
            key: string;
            hover: boolean;
            create: (
                entity: Promise<E | void>,
                entityId: string
            ) => (
                view: EditorView
            ) => Promise<Element | undefined> | Element | undefined;
        };
        // Prefix which is shown inline.
        prefix: string;
    },
    // All settings for inserting a new link.
    insert: {
        // The popup menu which can be triggered if a prefix or regexp are set.
        popup: {
            key: string;
        };
        // The prefix which triggers the insert popup menu when types. Use either prefix or regExp.
        prefix?: string;
        // The regex which triggers the insert popopup menu when found. Use either prefix or regExp.
        regExp?: RegExp;
    },
    // A method to get the manager which can find and convert the correct entities.
    getManager: (baseUrl: string) => LinkedEntitiesManager<E>,
    // Settings for the menu, if ommitted, no menu item will be created.
    menu?: Omit<MenuItemState, "type" | "getItems" | "items" | "name">
) => {
    /**
     * Create a regular expression for the given prefix.
     *
     * It consists of a prefix a query match group and is cut off
     * with either a whitespace char or a `]`. The block is added
     * so that a converted link with the prefix in the text will
     * also never match.
     */
    const insertRegexp =
        insert.regExp ??
        (insert.prefix &&
            new RegExp(`\\${insert.prefix}(?<query>[^\\s\\]\\~\\/]*)$`));

    const getManagerWithBaseUrl = () => getManager(baseUrl);

    return class EntityExtension extends BaseExtension {
        modifySchema = createLinkUpgradeSchemaModifier(
            nodeType,
            isLink,
            info.popup,
            info.prefix
        );

        plugins() {
            return [
                createInfoPlugin(nodeType, info.popup, getManagerWithBaseUrl),
                createInsertPlugin(
                    nodeType,
                    insert.popup,
                    getManagerWithBaseUrl,
                    insertRegexp
                ),
                // Plugin to intercept clicks.
                new Plugin({
                    view() {
                        return {
                            update(view) {
                                const links = view.dom.querySelectorAll(
                                    `a.linked-entity.${nodeType}`
                                );
                                links.forEach((link) => {
                                    if (!link.hasAttribute("data-listener")) {
                                        link.addEventListener(
                                            "click",
                                            (event) => {
                                                if (view.editable) {
                                                    event.preventDefault();
                                                }
                                            }
                                        );
                                        link.setAttribute(
                                            "data-listener",
                                            "true"
                                        );
                                    }
                                });
                            },
                        };
                    },
                }),
            ];
        }

        modifySerializer = createModifySerializer(
            nodeType,
            getManagerWithBaseUrl
        );

        upgradeParsedDocument = createUpgradeParsedDocument(
            nodeType,
            getManagerWithBaseUrl
        );

        menu = createMenu(nodeType, menu, insert.popup, getManagerWithBaseUrl);
    };
};
